import { WorkOrderQueueItem, WorkstationInfo, WorkOrderDetail, CompletedWorkItem, ProductDetails, PipelineOrder, PipelineShortage } from '../types';

// Re-export types for pages that import from api
export type { WorkOrderQueueItem, WorkstationInfo, WorkOrderDetail, CompletedWorkItem, ProductDetails, PipelineOrder, PipelineShortage };

// API_URL should include /api - matches docker-compose VITE_API_URL: /api
const API_URL = import.meta.env.VITE_API_URL || '/api';

// MobiPrint — downloads .zpl file, MobiPrint app on iPad sends to printer
import { sendToMobiPrint, queueAndPrint } from './printer';
export { sendToMobiPrint };

export const api = {
  // Generic helpers for pages that use api.get()/api.post() pattern
  async get(url: string): Promise<{ data: any }> {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const response = await fetch(fullUrl, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      throw new Error(`GET ${url} failed: ${response.status}`);
    }
    const data = await response.json();
    return { data };
  },

  async post(url: string, body?: any): Promise<{ data: any }> {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      throw new Error(`POST ${url} failed: ${response.status}`);
    }
    const data = await response.json();
    return { data };
  },

  async getWorkstations(): Promise<WorkstationInfo[]> {
    const response = await fetch(`${API_URL}/production/workstations?portal=factory`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch workstations');
    }
    return response.json();
  },

  async getMyQueue(workstationId: string): Promise<WorkOrderQueueItem[]> {
    const response = await fetch(`${API_URL}/production/queue/${workstationId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getComingUp(workstationId: string): Promise<WorkOrderQueueItem[]> {
    const response = await fetch(`${API_URL}/production/coming-up/${workstationId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getProductionCalendar(): Promise<{ scheduled: any[]; unscheduled: any[] }> {
    const response = await fetch(`${API_URL}/production/calendar`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { scheduled: [], unscheduled: [] };
    return response.json();
  },

  async getProductionCalendarStats(): Promise<{ total: number; rush: number; overdue: number; unscheduled: number }> {
    const response = await fetch(`${API_URL}/production/calendar/stats`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { total: 0, rush: 0, overdue: 0, unscheduled: 0 };
    return response.json();
  },

  async getHistory(workstationId: string, days = 30): Promise<CompletedWorkItem[]> {
    const response = await fetch(`${API_URL}/production/history/${workstationId}?days=${days}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getWorkOrdersByOrder(orderId: string): Promise<any[]> {
    const response = await fetch(`${API_URL}/production/work-orders/by-order/${orderId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getWorkOrderDetail(workOrderId: string): Promise<WorkOrderDetail> {
    const response = await fetch(`${API_URL}/production/work-orders/${workOrderId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      throw new Error(`Work order not found: ${response.status}`);
    }
    return response.json();
  },

  async startWorkOrder(workOrderId: string): Promise<void> {
    const response = await fetch(`${API_URL}/production/work-orders/${workOrderId}/start`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error(`Failed to start work order: ${response.status}`);
  },

  async completeWorkOrder(workOrderId: string): Promise<void> {
    const response = await fetch(`${API_URL}/production/work-orders/${workOrderId}/complete`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error(`Failed to complete work order: ${response.status}`);
  },

  async pauseWorkOrder(workOrderId: string): Promise<void> {
    const response = await fetch(`${API_URL}/production/work-orders/${workOrderId}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error(`Failed to pause work order: ${response.status}`);
  },

  async skipWorkOrder(workOrderId: string, reason: string): Promise<void> {
    const response = await fetch(`${API_URL}/production/work-orders/${workOrderId}/skip`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reason })
    });
    if (!response.ok) throw new Error(`Failed to skip work order: ${response.status}`);
  },

  async queueLabel(workOrderId: string, labelType: string, copies: number = 1): Promise<void> {
    await fetch(`${API_URL}/production/work-orders/${workOrderId}/labels`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ labelType, copies })
    });
  },

  async queueAndPrintLabel(workOrderId: string, labelType: string, workstationId?: string): Promise<{ success: boolean; message: string }> {
    return queueAndPrint(workOrderId, labelType, workstationId);
  },

  async getWorkstationLabelType(workstationCode: string): Promise<string> {
    const res = await fetch(`${API_URL}/labels/workstation-label-type/${encodeURIComponent(workstationCode)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!res.ok) return 'final_product';
    const data = await res.json();
    return data.labelType || 'final_product';
  },

  // Employee Dashboard APIs
  async getTodaysAssignment(): Promise<any> {
    const response = await fetch(`${API_URL}/employee-shifts/today`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { assigned: false };
    return response.json();
  },

  async getWeeklyHours(weekStart: string): Promise<any> {
    const response = await fetch(`${API_URL}/employee-shifts/weekly-hours?week_start=${weekStart}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { totalHours: 0 };
    return response.json();
  },

  async getMyTimeHistory(days = 14): Promise<any[]> {
    const response = await fetch(`${API_URL}/employee-shifts/my-time-history?days=${days}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getTodaysPunches(): Promise<any[]> {
    const response = await fetch(`${API_URL}/employee-shifts/todays-punches`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getCurrentStreak(): Promise<any> {
    const response = await fetch(`${API_URL}/employee-shifts/streak`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { streak: 0 };
    return response.json();
  },

  async getPointsBalance(): Promise<any> {
    const response = await fetch(`${API_URL}/employee-rewards/balance`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { balance: 0 };
    return response.json();
  },

  async getPTOBalances(): Promise<any[]> {
    const response = await fetch(`${API_URL}/time-off/balances`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // Schedule/Calendar APIs
  async getMyShifts(month?: number, year?: number): Promise<any[]> {
    let url = `${API_URL}/employee-shifts/my-shifts`;
    if (month && year) url += `?month=${month}&year=${year}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getUpcomingTimeOff(): Promise<any[]> {
    const response = await fetch(`${API_URL}/time-off/upcoming`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // Time Off APIs
  async getTimeOffRequests(status?: string): Promise<any[]> {
    let url = `${API_URL}/time-off/requests`;
    if (status) url += `?status=${status}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async createTimeOffRequest(data: any): Promise<any> {
    const response = await fetch(`${API_URL}/time-off/requests`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error(`Failed to create time off request: ${response.status}`);
    return response.json();
  },

  // Support requests (Phase A). Multipart so the optional photo rides along.
  async createSupportRequest(fields: {
    description: string; canKeepWorking: boolean; currentOrderStopped: boolean;
    workstationId?: string; workOrderId?: string; orderId?: string;
  }, photo?: File | null): Promise<any> {
    const fd = new FormData();
    fd.append('description', fields.description);
    fd.append('canKeepWorking', String(fields.canKeepWorking));
    fd.append('currentOrderStopped', String(fields.currentOrderStopped));
    if (fields.workstationId) fd.append('workstationId', fields.workstationId);
    if (fields.workOrderId) fd.append('workOrderId', fields.workOrderId);
    if (fields.orderId) fd.append('orderId', fields.orderId);
    if (photo) fd.append('photo', photo);
    const response = await fetch(`${API_URL}/support-requests`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }, // no Content-Type: browser sets multipart boundary
      body: fd,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(Array.isArray(err.message) ? err.message.join('; ') : err.message || `Failed to submit (${response.status})`);
    }
    return response.json();
  },

  async getMySupportRequests(): Promise<any[]> {
    const response = await fetch(`${API_URL}/support-requests/mine`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // ---- Development reviews (employee side) ----
  // Password re-entry uses the shared discipline records unlock (one password
  // check in the whole codebase). Returns a short-lived token presented as
  // X-Records-Unlock on the review content calls below.
  async reviewsUnlock(password: string): Promise<string | null> {
    const res = await fetch(`${API_URL}/discipline/my-records/unlock`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) return null;
    return (await res.json()).unlockToken || null;
  },
  async getMyReviews(unlockToken: string): Promise<any[]> {
    const res = await fetch(`${API_URL}/reviews/mine`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'X-Records-Unlock': unlockToken } });
    if (!res.ok) return [];
    return res.json();
  },
  async getMyReviewsPendingCount(): Promise<number> {
    try {
      const res = await fetch(`${API_URL}/reviews/mine/pending-count`, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });
      if (!res.ok) return 0;
      return (await res.json()).count || 0;
    } catch { return 0; }
  },
  async submitReviewInput(id: string, answers: any, unlockToken: string): Promise<any> {
    const res = await fetch(`${API_URL}/reviews/mine/${id}/input`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'X-Records-Unlock': unlockToken },
      body: JSON.stringify(answers),
    });
    if (!res.ok) throw new Error('Could not save your input');
    return res.json();
  },
  async acknowledgeReview(id: string, comment: string, unlockToken: string): Promise<any> {
    const res = await fetch(`${API_URL}/reviews/mine/${id}/acknowledge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}`, 'X-Records-Unlock': unlockToken },
      body: JSON.stringify({ comment }),
    });
    if (!res.ok) throw new Error('Could not acknowledge');
    return res.json();
  },

  // Clears the "responded to" badge — called when the employee opens My Requests.
  async markSupportRequestsSeen(): Promise<void> {
    try {
      await fetch(`${API_URL}/support-requests/mine/mark-seen`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
    } catch { /* non-critical */ }
  },

  async cancelTimeOffRequest(id: string): Promise<void> {
    await fetch(`${API_URL}/time-off/requests/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
  },

  // Training APIs
  async getMyTraining(params?: any): Promise<any[]> {
    let url = `${API_URL}/training/my-training`;
    if (params) {
      const query = new URLSearchParams(params).toString();
      url += `?${query}`;
    }
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getAvailableTraining(): Promise<any[]> {
    const response = await fetch(`${API_URL}/training/available`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getRequiredTraining(): Promise<any[]> {
    const response = await fetch(`${API_URL}/training/required`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getCertifications(): Promise<any[]> {
    const response = await fetch(`${API_URL}/training/certifications`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async enrollInTraining(moduleId: string): Promise<any> {
    const response = await fetch(`${API_URL}/training/enroll/${moduleId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error(`Failed to enroll: ${response.status}`);
    return response.json();
  },

  async startTraining(moduleId: string): Promise<any> {
    const response = await fetch(`${API_URL}/training/start/${moduleId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error(`Failed to start training: ${response.status}`);
    return response.json();
  },

  async completeTraining(moduleId: string, data?: any): Promise<any> {
    const response = await fetch(`${API_URL}/training/complete/${moduleId}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data || {})
    });
    if (!response.ok) throw new Error(`Failed to complete training: ${response.status}`);
    return response.json();
  },

  // Documents APIs — always scoped to the workstation portal, so the shop-floor
  // Documents page only ever shows 'employees' docs, never office/dealer docs
  // (even for an admin logged into the PWA).
  async getMyDocuments(params?: any): Promise<any[]> {
    const query = new URLSearchParams({ ...(params || {}), portal: 'workstation' }).toString();
    const response = await fetch(`${API_URL}/sop-documents/my-documents?${query}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getFavoriteDocuments(): Promise<any[]> {
    const response = await fetch(`${API_URL}/sop-documents/favorites?portal=workstation`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async addDocumentToFavorites(documentId: string): Promise<void> {
    await fetch(`${API_URL}/sop-documents/favorites/${documentId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
  },

  async removeDocumentFromFavorites(documentId: string): Promise<void> {
    await fetch(`${API_URL}/sop-documents/favorites/${documentId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
  },

  async getDocumentCategories(): Promise<string[]> {
    const response = await fetch(`${API_URL}/sop-documents/categories?portal=workstation`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // Rewards APIs
  async getRewardsCatalog(params?: any): Promise<any[]> {
    let url = `${API_URL}/employee-rewards/catalog`;
    if (params) {
      const query = new URLSearchParams(params).toString();
      url += `?${query}`;
    }
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getPointsTransactions(params?: any): Promise<any[]> {
    let url = `${API_URL}/employee-rewards/transactions`;
    if (params) {
      const query = new URLSearchParams(params).toString();
      url += `?${query}`;
    }
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getMonthlyPointsStats(month: number, year: number): Promise<any> {
    const response = await fetch(`${API_URL}/employee-rewards/monthly-stats?month=${month}&year=${year}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return {};
    return response.json();
  },

  async getRewardsLeaderboard(params?: any): Promise<any[]> {
    let url = `${API_URL}/employee-rewards/leaderboard`;
    if (params) {
      const query = new URLSearchParams(params).toString();
      url += `?${query}`;
    }
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async redeemReward(rewardId: string): Promise<any> {
    const response = await fetch(`${API_URL}/employee-rewards/redeem/${rewardId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error(`Failed to redeem reward: ${response.status}`);
    return response.json();
  },

  async getMyRedemptions(): Promise<any[]> {
    const response = await fetch(`${API_URL}/employee-rewards/my-redemptions`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // BOM/Cut Sheet APIs
  async getCutSheet(orderId: string): Promise<CutSheetItem[]> {
    const response = await fetch(`${API_URL}/bom/cut-sheet/${orderId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getBomItems(orderId: string, workstation?: string, componentType?: string): Promise<BomItem[]> {
    let url = `${API_URL}/bom/items/${orderId}`;
    const params = new URLSearchParams();
    if (workstation) params.append('workstation', workstation);
    if (componentType) params.append('componentType', componentType);
    if (params.toString()) url += `?${params.toString()}`;

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async consumeBomItems(orderId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(`${API_URL}/bom/consume/${orderId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    return response.json();
  },

  async getPositioning(orderItemId: string): Promise<PositioningData | null> {
    try {
      const response = await fetch(`${API_URL}/bom/positioning/${orderItemId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // ============ Inventory Drops APIs ============

  async createDrop(data: CreateDropRequest): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create drop');
    return response.json();
  },

  async createFabricDrop(data: {
    partNumber: string;
    fabricType: string;
    fabricColor: string;
    lengthInches: number;
    dropHeight: number;
    originalRollWidth: number;
    workOrderId?: string;
    orderNumber?: string;
    binLocation?: string;
    notes?: string;
  }): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/fabric`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create fabric drop');
    return response.json();
  },

  async getAllWorkstations(): Promise<WorkstationInfo[]> {
    const response = await fetch(`${API_URL}/production/workstations-all?includeInactive=true`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getAvailableDrops(partType: string, minLength?: number): Promise<InventoryDrop[]> {
    let url = `${API_URL}/inventory/drops/available/${encodeURIComponent(partType)}`;
    if (minLength) url += `?minLength=${minLength}`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getDropById(id: string): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error('Drop not found');
    return response.json();
  },

  async getDropByDropId(dropId: string): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/by-drop-id/${dropId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error('Drop not found');
    return response.json();
  },

  async updateDropLocation(id: string, binLocation: string): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}/location`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ binLocation })
    });
    if (!response.ok) throw new Error('Failed to update drop location');
    return response.json();
  },

  async useDrop(id: string, data: { workOrderId?: string; orderNumber?: string }): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}/use`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to mark drop as used');
    return response.json();
  },

  async scanDropBarcode(barcode: string): Promise<{ type: 'drop' | 'bin' | 'unknown'; data: any }> {
    const response = await fetch(`${API_URL}/inventory/drops/scan/${encodeURIComponent(barcode)}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { type: 'unknown', data: null };
    return response.json();
  },

  async getDropStats(): Promise<DropStats> {
    const response = await fetch(`${API_URL}/inventory/drops/stats`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      // Return default stats on error
      return {
        totalDrops: 0,
        availableDrops: 0,
        usedDrops: 0,
        wasteDrops: 0,
        totalAvailableLength: 0,
        byPartType: []
      };
    }
    return response.json();
  },

  async getDropPartTypes(): Promise<string[]> {
    const response = await fetch(`${API_URL}/inventory/drops/part-types`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // ============ Inventory Management APIs ============

  async getInventoryGroups(): Promise<{ name: string; itemCount: number; totalValue: number }[]> {
    const response = await fetch(`${API_URL}/inventory/groups`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      if (response.status === 401) throw new Error('Session expired. Please log in again.');
      return [];
    }
    return response.json();
  },

  async getWorkstationInventoryItems(params: {
    search?: string;
    group?: string;
    lowStockOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ items: any[]; total: number; limit: number; offset: number }> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.group) query.set('group', params.group);
    if (params.lowStockOnly) query.set('lowStockOnly', 'true');
    if (params.limit) query.set('limit', String(params.limit));
    if (params.offset) query.set('offset', String(params.offset));
    const response = await fetch(`${API_URL}/inventory/workstation/items?${query.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      if (response.status === 401) throw new Error('Session expired. Please log in again.');
      return { items: [], total: 0, limit: params.limit || 50, offset: params.offset || 0 };
    }
    return response.json();
  },

  async workstationAdjustStock(data: {
    itemId: string;
    quantity: number;
    adjustmentType: 'add' | 'subtract' | 'set';
    reason: string;
    notes?: string;
  }): Promise<any> {
    const response = await fetch(`${API_URL}/inventory/workstation/adjust`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to adjust stock');
    }
    return response.json();
  },

  async workstationReceiveStock(data: {
    itemId: string;
    quantity: number;
    quantityInDisplayUnits?: boolean;
    poNumber?: string;
    notes?: string;
  }): Promise<any> {
    const response = await fetch(`${API_URL}/inventory/workstation/receive`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to receive stock');
    }
    return response.json();
  },

  // Fabric Settings APIs
  async getFabricSettings(fabricType: string): Promise<FabricSettings | null> {
    try {
      const response = await fetch(`${API_URL}/fabric-settings/${encodeURIComponent(fabricType)}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  },

  // Label Printing APIs
  async generateDropLabel(data: {
    dropId: string;
    partType: string;
    length: number;
    height?: number;
    width?: number;
    partNumber?: string;
    binLocation?: string;
    color?: string;
  }): Promise<{ dropId: string; zpl: string }> {
    const response = await fetch(`${API_URL}/bins/labels/drop`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to generate drop label');
    return response.json();
  },

  // ============ Drop Calculation & Repair ============

  async calculateDropInfo(workOrderId: string): Promise<DropCalculation[]> {
    try {
      const response = await fetch(`${API_URL}/inventory/drops/calculate/${workOrderId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      });
      if (!response.ok) return [];
      return response.json();
    } catch {
      return [];
    }
  },

  async markDropNeedsRepair(id: string, reason: string, vendor?: string): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}/needs-repair`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({ reason, vendor })
    });
    if (!response.ok) throw new Error('Failed to mark drop as needs repair');
    return response.json();
  },

  async markDropRepairSent(id: string, vendor: string): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}/repair-sent`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify({ vendor })
    });
    if (!response.ok) throw new Error('Failed to mark repair as sent');
    return response.json();
  },

  async markDropRepairComplete(id: string): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}/repair-complete`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`
      },
    });
    if (!response.ok) throw new Error('Failed to mark repair as complete');
    return response.json();
  },

  async createRepairItem(data: {
    partNumber: string;
    partType: string;
    lengthInches: number;
    repairReason: string;
    repairVendor?: string;
    notes?: string;
  }): Promise<InventoryDrop> {
    const response = await fetch(`${API_URL}/inventory/drops/repair-item`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`
      },
      body: JSON.stringify(data)
    });
    if (!response.ok) throw new Error('Failed to create repair item');
    return response.json();
  },

  async deleteDrop(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/inventory/drops/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to delete drop');
    }
  },

  // ============ Parts Pull APIs ============

  async getPartsPullQueue(pullType: 'bin' | 'shelves'): Promise<PartsPullQueueItem[]> {
    const response = await fetch(`${API_URL}/parts-pull/queue/${pullType}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getPartsPullForOrder(orderId: string): Promise<{ bin: PartsPull | null; shelves: PartsPull | null }> {
    const response = await fetch(`${API_URL}/orders/${orderId}/parts-pull`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return { bin: null, shelves: null };
    return response.json();
  },

  async startPartsPull(orderId: string, pullType: 'bin' | 'shelves'): Promise<PartsPull> {
    const response = await fetch(`${API_URL}/orders/${orderId}/parts-pull/${pullType}/start`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    });
    if (!response.ok) throw new Error('Failed to start parts pull');
    return response.json();
  },

  async markPartPulled(itemId: string, quantityPulled: number): Promise<PartsPullItem> {
    const response = await fetch(`${API_URL}/parts-pull/items/${itemId}/pulled`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ quantity: quantityPulled })
    });
    if (!response.ok) throw new Error('Failed to mark item pulled');
    return response.json();
  },

  async reportPartShortage(itemId: string, shortageQuantity: number, notes?: string): Promise<PartsPullItem> {
    const response = await fetch(`${API_URL}/parts-pull/items/${itemId}/shortage`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ shortageQuantity, notes })
    });
    if (!response.ok) throw new Error('Failed to report shortage');
    return response.json();
  },

  async completePartsPull(orderId: string, pullType: 'bin' | 'shelves', binColor: 'black' | 'red'): Promise<PartsPull> {
    const response = await fetch(`${API_URL}/orders/${orderId}/parts-pull/${pullType}/complete`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ binColor })
    });
    if (!response.ok) throw new Error('Failed to complete parts pull');
    return response.json();
  },

  // ============ Workflow Progress APIs ============

  async getOrderWorkflowProgress(orderId: string): Promise<OrderWorkflowProgress> {
    const response = await fetch(`${API_URL}/workflow/orders/${orderId}/progress`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error('Failed to load workflow progress');
    return response.json();
  },

  async startWorkflowStep(progressId: string): Promise<WorkflowProgressItem> {
    const response = await fetch(`${API_URL}/workflow/progress/${progressId}/start`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) throw new Error('Failed to start step');
    return response.json();
  },

  async completeWorkflowStep(progressId: string, notes?: string): Promise<WorkflowProgressItem> {
    const response = await fetch(`${API_URL}/workflow/progress/${progressId}/complete`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ notes })
    });
    if (!response.ok) throw new Error('Failed to complete step');
    return response.json();
  },

  // ============ Orders in Production ============

  async getOrdersInProduction(): Promise<InProductionOrder[]> {
    const response = await fetch(`${API_URL}/production/orders-in-production`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // ============ Company Events APIs ============

  async getUpcomingEvents(limit: number = 5): Promise<CompanyEvent[]> {
    const response = await fetch(`${API_URL}/company-events/upcoming?limit=${limit}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getEmployeeEvents(startDate?: string, endDate?: string): Promise<CompanyEvent[]> {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const response = await fetch(`${API_URL}/company-events/employee?${params.toString()}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getHolidays(year?: number): Promise<CompanyEvent[]> {
    const params = year ? `?year=${year}` : '';
    const response = await fetch(`${API_URL}/company-events/holidays${params}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  // Pipeline (Coming Up - pre-production orders)
  async getPipelineOrders(): Promise<PipelineOrder[]> {
    const response = await fetch(`${API_URL}/production/pipeline`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return [];
    return response.json();
  },

  async checkPipelineShortages(orderIds: string[]): Promise<PipelineShortage[]> {
    const response = await fetch(`${API_URL}/production/pipeline/check-shortages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
      },
      body: JSON.stringify({ orderIds }),
    });
    if (!response.ok) return [];
    return response.json();
  },

  async getOrderShortages(orderId: string): Promise<PipelineShortage | null> {
    const response = await fetch(`${API_URL}/production/pipeline/${orderId}/shortages`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
    });
    if (!response.ok) return null;
    return response.json();
  }
};

export interface FabricSettings {
  id: string;
  fabricType: string;
  fabricCategory: string | null;
  weldTempHot: number | null;
  weldTempCold: number | null;
  weldSpeed: number | null;
  weldPressure: number | null;
  weldNotes: string | null;
  cutBladeType: string | null;
  cutSpeed: number | null;
  cutPressure: number | null;
  cutPasses: number;
  cutNotes: string | null;
}

// BOM Types
export interface CutSheetItem {
  orderItemId: string;
  itemNumber?: number;
  systemWidth: number;
  systemHeight: number;
  calculatedDimensions?: {
    systemWidth: number;
    systemHeight: number;
    uChannelDeduction: number;
    boxDeduction: number;
    boxWidthAdjustment: number;
    dealerBoxAdjustment: number;
    widthSelected: number;
    widthTop?: number;
  };
  cutSizes: {
    fabricWidth: number;
    fabricHeight: number;
    weightBar: number;
    rollerTube: number;
    housingMain: number;
    housingCover: number;
    vTrack: number;
    uChannel?: number;
    kederGuide?: number;
    screwCover?: number;
  };
  springHoles: {
    count: number;
    spacing: number;
    positions: number[];
    positionsFormatted: string[];
  };
  seamRequired: boolean;
  seamDetails?: {
    panel1Height: number;
    panel2Height: number;
    totalHeight: number;
  };
  dropAllocations?: any[];
}

export interface BomItem {
  id: string;
  orderItemId: string;
  itemNumber?: number;
  partNumber: string;
  description: string;
  quantity: number;
  cutLength?: number | null;
  unit: string;
  workstation: string;
  componentType: string;
  allocated: boolean;
  consumed: boolean;
  stockOnHand?: number | null;
  imageUrl?: string | null;
}

// Inventory Drops Types
export interface InventoryDrop {
  id: string;
  dropId: string;
  partNumber: string;
  partType: string;
  lengthInches: number;
  originalStockLength: number;
  status: 'available' | 'reserved' | 'used' | 'waste' | 'needs_repair';
  binLocation: string | null;
  workOrderId: string | null;
  orderNumber: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  usedAt: string | null;
  usedBy: string | null;
  usedByName: string | null;
  usedInOrderNumber: string | null;
  notes: string | null;
  // Repair fields
  repairReason: string | null;
  repairVendor: string | null;
  repairSentAt: string | null;
  repairReturnedAt: string | null;
  source: string | null;
}

export interface DropCalculation {
  partType: string;
  partNumber: string;
  calculatedDropLength: number;
  stockLength: number;
  cutLength: number;
  usesDrop: boolean;
  sourceDropId: string | null;
}

export interface CreateDropRequest {
  partNumber: string;
  partType: string;
  lengthInches: number;
  originalStockLength?: number;
  workOrderId?: string;
  orderNumber?: string;
  binLocation?: string;
  notes?: string;
}

export interface DropStats {
  totalDrops: number;
  availableDrops: number;
  usedDrops: number;
  wasteDrops: number;
  repairDrops: number;
  totalAvailableLength: number;
  byPartType: { partType: string; count: number; totalLength: number }[];
}

// Positioning Types for BOM Assembly Items
export interface PartPositioning {
  partNumber: string;
  description: string;
  assemblyType: string;
  motorSidePosition: number;
  idlerSidePosition: number;
  effectiveMotorPosition: number;
  effectiveIdlerPosition: number;
  positionDeductionApplies: boolean;
  positionDeductionMethod: 'split_evenly' | 'motor_only' | 'idler_only' | 'none';
  factoryDeduction: number;
  notes?: string;
}

export interface PositioningData {
  orderItemId: string;
  hasPositionedParts: boolean;
  factoryDeductionTotal: number;
  parts: PartPositioning[];
}

// Parts Pull Types
export interface PartsPullItem {
  id: string;
  partNumber: string;
  description: string;
  quantityNeeded: number;
  quantityPulled: number;
  pulled: boolean;
  shortage: boolean;
  shortageQuantity: number | null;
  binLocation: string | null;
  componentType: string;
  imageUrl: string | null;
}

export interface PartsPull {
  id: string;
  orderId: string;
  orderNumber: string;
  pullType: 'bin' | 'shelves';
  status: 'pending' | 'in_progress' | 'complete' | 'shortage';
  binColor: string | null;
  assignedBinId: string | null;
  items: PartsPullItem[];
  startedAt: string | null;
  completedAt: string | null;
}

export interface PartsPullQueueItem {
  id: string;
  orderId: string;
  orderNumber: string;
  companyName: string;
  sidemark: string;
  pullType: 'bin' | 'shelves';
  status: string;
  unitCount: number;
  itemCount: number;
  isRush: boolean;
}

// Workflow Progress Types
export interface WorkflowProgressItem {
  id: string;
  orderId: string;
  orderItemId: string | null;
  stepName: string;
  stepDisplayName: string;
  category: string;
  status: 'pending' | 'blocked' | 'ready' | 'in_progress' | 'complete' | 'skipped';
  blockedReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  completedByName: string | null;
  workOrderId?: string | null;
  workstationId?: string | null;
  itemNumber?: number | null;
}

export interface InProductionOrder {
  id: string;
  orderNumber: string;
  sidemark: string;
  companyName: string;
  status: string;
  shippingMethod?: string;
  totalItems: number;
  completedItems: number;
  totalWorkOrders: number;
  completedWorkOrders: number;
  inProgressWorkOrders: number;
  progress: number;
}

export interface OrderWorkflowProgress {
  orderId: string;
  orderNumber: string;
  overallProgress: {
    completed: number;
    total: number;
    percentage: number;
  };
  steps: WorkflowProgressItem[];
}

// Company Events
export interface CompanyEvent {
  id: string;
  title: string;
  description: string | null;
  eventType: string;
  startTime: string;
  endTime: string | null;
  allDay: boolean;
  location: string | null;
  virtualLink: string | null;
  visibility: string;
  department: string | null;
  status: string;
  color: string | null;
}

export default api;
